import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getTicketTypeOptions } from '@/modules/Ticket/services'
import { TicketType } from '@/modules/Ticket/types/response'
import React, { useEffect, useMemo, useState } from 'react'
import { Control } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FormItem } from 'vnetwork-ui'
import FormLabel from '@/components/Form/FormLabel'
import TicketSLAInfo from '../TicketSLAInfo'

// Help content per group
import helpServiceRequest from '@/modules/Ticket/help/ticket-types/service_request.md'
import helpErrorService from '@/modules/Ticket/help/ticket-types/error_service.md'
import helpSupportRequest from '@/modules/Ticket/help/ticket-types/support_request.md'
import helpService from '@/modules/Ticket/help/ticket-types/service.md'
import helpMeeting from '@/modules/Ticket/help/ticket-types/meeting.md'
import helpChange from '@/modules/Ticket/help/ticket-types/change.md'
import helpInternal from '@/modules/Ticket/help/ticket-types/internal.md'
import helpDefault from '@/modules/Ticket/help/ticket-type-help.md'

const HELP_CONTENT_MAP: Record<string, string> = {
    service_request: helpServiceRequest,
    error_service: helpErrorService,
    support_request: helpSupportRequest,
    service: helpService,
    meeting: helpMeeting,
    change: helpChange,
    internal: helpInternal,
}

interface Props {
    control: Control<any>
    disabled?: boolean
    handleChangeTicketType: (
        ticketType: Partial<TicketType & { value: number; label: string }>
    ) => void
    defaultTicketType?: string
    ticketTypeGroup?: string
    onTicketTypeGroupDetected?: (group: string) => void
    hasJourneyId?: boolean
}

const TicketTypeC: React.FC<Props> = ({
    control,
    handleChangeTicketType,
    disabled = false,
    defaultTicketType,
    ticketTypeGroup,
    onTicketTypeGroupDetected,
    hasJourneyId = false,
}) => {
    const { t } = useTranslation()
    const [selectedType, setSelectedType] =
        useState<Partial<TicketType> | null>(null)

    // Get help content based on selected group
    const helpContent = useMemo(() => {
        if (ticketTypeGroup && HELP_CONTENT_MAP[ticketTypeGroup]) {
            return HELP_CONTENT_MAP[ticketTypeGroup]
        }
        return helpDefault
    }, [ticketTypeGroup])

    // Fetch all ticket types when there's a defaultTicketType (to auto-detect group)
    const { data: allTicketTypes } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.TICKET_TYPES, 'all-for-default'],
        queryFn: () => getTicketTypeOptions(),
        enabled: !!defaultTicketType && !ticketTypeGroup,
    })

    // Fetch ticket types filtered by group
    const { data, renderState, isFetching, refetch } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.TICKET_TYPES, ticketTypeGroup],
        queryFn: () => getTicketTypeOptions(ticketTypeGroup),
        enabled: !!ticketTypeGroup,
    })

    useEffect(() => {
        if (ticketTypeGroup) {
            refetch()
        }
    }, [ticketTypeGroup, refetch])

    // Auto-detect ticket_type_group from defaultTicketType
    useEffect(() => {
        if (defaultTicketType && allTicketTypes && !ticketTypeGroup) {
            const ticketType = allTicketTypes.find(
                (type) => type.ticket_type_key === defaultTicketType
            )
            if (ticketType?.ticket_type_group && onTicketTypeGroupDetected) {
                onTicketTypeGroupDetected(ticketType.ticket_type_group)
            }
        }
    }, [
        defaultTicketType,
        allTicketTypes,
        ticketTypeGroup,
        onTicketTypeGroupDetected,
    ])

    // Set default ticket type after group is set and data is loaded
    useEffect(() => {
        if (defaultTicketType && data && ticketTypeGroup) {
            const ticketType = data.find(
                (type) => type.ticket_type_key === defaultTicketType
            )
            if (ticketType) {
                setSelectedType(ticketType)
                handleChangeTicketType(ticketType)
            }
        }
    }, [defaultTicketType, data, ticketTypeGroup])

    // Reset selected type when group changes
    useEffect(() => {
        setSelectedType(null)
    }, [ticketTypeGroup])

    // Filter options: ẩn ticket types có key "meeting" nếu không có journey ID
    const filteredOptions = useMemo(() => {
        if (!data) return []
        if (hasJourneyId) return data
        return data.filter((item) => item.ticket_type_key !== 'meeting')
    }, [data, hasJourneyId])

    if (!ticketTypeGroup) {
        return (
            <div>
                <FormLabel
                    label={t('ticket-type')}
                    helpContent={helpContent}
                    helpTitle={t('ticket-type-help')}
                />
                <FormItem
                    control={control}
                    name="ticket_type_id"
                    type="select"
                    placeholder={t('ticket-type-select-group-first')}
                    options={[]}
                    disabled={true}
                />
            </div>
        )
    }

    if (renderState()) return renderState()

    return (
        <div className="space-y-2">
            <div>
                <FormLabel
                    label={t('ticket-type')}
                    helpContent={helpContent}
                    helpTitle={t('ticket-type-help')}
                />
                <FormItem
                    control={control}
                    name="ticket_type_id"
                    type="select"
                    placeholder={t('ticket-type')}
                    options={filteredOptions}
                    returnFullOption
                    onChangeAfterHookForm={(ticketType) => {
                        setSelectedType(ticketType)
                        handleChangeTicketType(ticketType)
                    }}
                    allowClear={false}
                    disabled={disabled || isFetching}
                    loading={isFetching}
                />
            </div>
            <TicketSLAInfo
                time={selectedType?.ticket_type_time}
                timeFormat={selectedType?.ticket_type_time_format}
            />
        </div>
    )
}

export default TicketTypeC
